import React, { useEffect, useState } from 'react';
import { ExternalLink, Building2, ArrowRight, Briefcase } from 'lucide-react';
import api from './api';

const PLACEHOLDER_IMG = null;

const ReferenceCard = ({ reference, index }) => {
  const [hovered, setHovered] = useState(false);

  const colors = [
    { accent: '#E8611A', light: 'rgba(232,97,26,0.08)', border: 'rgba(232,97,26,0.2)' },
    { accent: '#3b82f6', light: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)' },
    { accent: '#16a34a', light: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.2)' },
  ];
  const color = colors[index % colors.length];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.80)',
        backdropFilter: 'blur(14px)',
        border: hovered ? `1px solid ${color.border}` : '1px solid rgba(0,0,0,0.06)',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform .25s, box-shadow .25s, border-color .25s, background .25s',
        transform: hovered ? 'translateY(-6px)' : 'none',
        boxShadow: hovered
          ? `0 20px 48px rgba(0,0,0,0.10), 0 0 0 1px ${color.border}`
          : '0 4px 16px rgba(0,0,0,0.06)',
        cursor: 'default',
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* Image ou placeholder */}
      <div style={{
        height: 180,
        background: reference.image
          ? `url(${reference.image}) center/cover no-repeat`
          : `linear-gradient(135deg, ${color.light.replace('0.08', '0.15')} 0%, rgba(248,250,252,1) 100%)`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {!reference.image && (
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: color.light,
            border: `1.5px solid ${color.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Building2 size={28} color={color.accent} />
          </div>
        )}
        {/* Numéro badge */}
        <div style={{
          position: 'absolute', top: 14, left: 14,
          width: 32, height: 32, borderRadius: '50%',
          background: color.accent, color: '#fff',
          fontSize: '0.75rem', fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 2px 8px ${color.accent}40`,
        }}>
          {String(index + 1).padStart(2, '0')}
        </div>
        {/* Lien externe badge */}
        {reference.lien && (
          <a
            href={reference.lien}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 14, right: 14,
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              transition: 'background .2s',
              textDecoration: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = color.accent; e.currentTarget.children[0].style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.9)'; e.currentTarget.children[0].style.color = color.accent; }}
          >
            <ExternalLink size={14} color={color.accent} style={{ transition: 'color .2s' }} />
          </a>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '22px 24px 24px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
        {/* Client pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '5px 12px', borderRadius: 999, width: 'fit-content',
          background: color.light, border: `1px solid ${color.border}`,
        }}>
          <Building2 size={11} color={color.accent} />
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: color.accent, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {reference.client}
          </span>
        </div>

        {/* Mission */}
        <p style={{
          fontSize: '0.875rem', color: '#475569', lineHeight: 1.7,
          margin: 0, flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {reference.mission}
        </p>

        {/* CTA lien */}
        {reference.lien && (
          <a href={reference.lien} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: '0.8rem', fontWeight: 600, color: color.accent,
            textDecoration: 'none', marginTop: 4,
            transition: 'gap .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.gap = '10px'; }}
          onMouseLeave={e => { e.currentTarget.style.gap = '6px'; }}
          >
            Voir le projet
            <ArrowRight size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

export default function ReferencesSection() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/aydees/liste-reference/')
      .then(res => setReferences((res.data.results || res.data || []).slice(0, 3)))
      .catch(() => setReferences([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && references.length === 0) return null;

  return (
    <section style={{
      background: 'linear-gradient(135deg,#fff7ed 0%,#ffffff 50%,#f8fafc 100%)',
      padding: '56px 0 52px',
      fontFamily: "'DM Sans', sans-serif",
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Dot pattern */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle,#e2e8f0 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
      {/* Blobs */}
      <div style={{ position: 'absolute', top: '-10%', right: '8%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(232,97,26,0.13) 0%,transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-10%', left: '5%', width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle,rgba(59,130,246,0.09) 0%,transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', borderRadius: 999, border: '1px solid rgba(232,97,26,0.25)', background: 'rgba(232,97,26,0.07)', width: 'fit-content' }}>
              <Briefcase size={12} color="#E8611A" />
              <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#E8611A', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Nos réalisations</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 800, color: '#1e293b', margin: 0, lineHeight: 1.25 }}>
              Ils nous font{' '}
              <span style={{ background: 'linear-gradient(to right,#E8611A,#d4501a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>confiance</span>
            </h2>
            <div style={{ width: 48, height: 3, borderRadius: 99, background: 'linear-gradient(to right,#E8611A,#16a34a)' }} />
          </div>

          <a
            href="/references"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '11px 22px', borderRadius: 10,
              border: '1.5px solid rgba(232,97,26,0.3)',
              background: 'rgba(232,97,26,0.06)',
              color: '#E8611A', fontWeight: 700, fontSize: '0.85rem',
              textDecoration: 'none', letterSpacing: '0.03em',
              transition: 'background .2s, border-color .2s, transform .2s, box-shadow .2s',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#E8611A'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#E8611A'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(232,97,26,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(232,97,26,0.06)'; e.currentTarget.style.color = '#E8611A'; e.currentTarget.style.borderColor = 'rgba(232,97,26,0.3)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Voir toutes nos références
            <ArrowRight size={15} />
          </a>
        </div>

        {/* Cards */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 20 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ height: 340, borderRadius: 20, background: 'rgba(0,0,0,0.05)', animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 20, alignItems: 'stretch' }}>
            {references.map((ref, i) => (
              <ReferenceCard key={ref.id} reference={ref} index={i} />
            ))}
          </div>
        )}

        {/* Bottom CTA mobile */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 36 }}>
          <a
            href="/references"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9,
              padding: '13px 32px', borderRadius: 12,
              background: '#E8611A', color: '#fff',
              fontWeight: 700, fontSize: '0.9rem',
              textDecoration: 'none', letterSpacing: '0.04em',
              boxShadow: '0 4px 20px rgba(232,97,26,0.3)',
              transition: 'background .2s, transform .2s, box-shadow .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#d4501a'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(232,97,26,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#E8611A'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,97,26,0.3)'; }}
          >
            Voir toutes nos références
            <ArrowRight size={16} />
          </a>
        </div>

      </div>
    </section>
  );
}
